import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import {
  CalendarRange,
  MessageCircle,
  CreditCard,
  Users,
  BarChart3,
  IdCard,
  Tag,
  Wallet,
} from "lucide-react";

const MENUS = [
  {
    label: "Services",
    columns: [
      {
        heading: "Streamline your bookings",
        links: [
          { icon: CalendarRange, title: "Smart calendar", description: "Drag-and-drop bookings & staff shifts" },
          { icon: MessageCircle, title: "WhatsApp confirmations", description: "Automatic reminders for clients" },
        ],
      },
      {
        heading: "Manage your business",
        links: [
          { icon: Users, title: "Client management", description: "Notes, tags, and visit history" },
          { icon: IdCard, title: "Staff scheduling", description: "Assign services to your team" },
        ],
      },
      {
        heading: "Grow revenue",
        links: [
          { icon: CreditCard, title: "Payments & deposits", description: "Cut down on no-shows" },
          { icon: BarChart3, title: "Business insights", description: "Revenue & busy-hour reports" },
        ],
      },
    ],
  },
  {
    label: "Pricing",
    columns: [
      {
        heading: "Plans",
        links: [
          { icon: Tag, title: "Price-list", description: "Plans and what's included", to: "/for-business/pricing" },
        ],
      },
      {
        heading: "Payments",
        links: [
          { icon: Wallet, title: "Payout", description: "How and when you get paid", to: "/for-business/payout" },
        ],
      },
    ],
  },
];

function DropdownMenu({ menu, openLabel, setOpenLabel }) {
  const isOpen = openLabel === menu.label;
  const closeTimer = useRef(null);

  const open = () => {
    clearTimeout(closeTimer.current);
    setOpenLabel(menu.label);
  };
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpenLabel((cur) => (cur === menu.label ? null : cur)), 120);
  };

  return (
    <div className="relative" onMouseEnter={open} onMouseLeave={scheduleClose}>
      <button
        type="button"
        onClick={() => setOpenLabel(isOpen ? null : menu.label)}
        aria-expanded={isOpen}
        className="flex items-center gap-1 text-sm font-medium text-white/85 transition-colors hover:text-white"
      >
        {menu.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute left-1/2 top-[calc(100%+20px)] z-50 w-[640px] max-w-[90vw] -translate-x-1/2 rounded-xl border border-white/10 bg-slate-900 shadow-2xl">
          <div className="grid gap-8 px-6 py-8 sm:grid-cols-2 lg:grid-cols-3">
            {menu.columns.map((col) => (
              <div key={col.heading}>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{col.heading}</p>
                <ul className="mt-4 space-y-4">
                  {col.links.map(({ icon: Icon, title, description, to }) => (
                    <li key={title}>
                      <Link
                        to={to || "/for-business"}
                        onClick={() => setOpenLabel(null)}
                        className="group flex items-start gap-3 rounded-lg p-2 -m-2 transition-colors hover:bg-white/5"
                      >
                        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                          <Icon className="h-4 w-4" />
                        </span>
                        <span>
                          <span className="block text-sm font-semibold text-white">{title}</span>
                          <span className="block text-xs text-slate-400">{description}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ForBusinessNavbar() {
  const [openLabel, setOpenLabel] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenLabel(null);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  return (
    <header ref={navRef} className="sticky top-0 z-40 border-b border-white/10 bg-slate-900">
      <div className="mx-auto flex h-20 max-w-6xl items-center gap-8 px-4 sm:h-24 sm:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <img src="/logo.png" alt="SHUWA" className="h-12 w-auto object-contain brightness-0 invert sm:h-16" />
        </Link>

        <nav className="hidden items-center gap-7 md:flex">
          <Link to="/for-business" className="text-sm font-medium text-white/85 transition-colors hover:text-white">
            Why SHUWA
          </Link>
          {MENUS.map((menu) => (
            <DropdownMenu key={menu.label} menu={menu} openLabel={openLabel} setOpenLabel={setOpenLabel} />
          ))}
        </nav>

        <div className="ml-auto flex shrink-0 items-center gap-3">
          <Link to="/login?next=/business" className="hidden text-sm font-medium text-white/85 hover:text-white sm:inline">
            Log in
          </Link>
          <Link
            to="/register?role=business_owner&type=business&next=/business/onboarding"
            className="hidden rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 sm:inline-block"
          >
            Try it for free
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
            className="flex h-9 w-9 items-center justify-center rounded-full text-white md:hidden"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-white/10 bg-slate-900 px-4 py-4 md:hidden">
          <div className="flex flex-col gap-1">
            <Link
              to="/for-business"
              onClick={() => setMobileOpen(false)}
              className="rounded-xl px-3 py-2.5 text-sm font-medium text-white/85 hover:bg-white/5"
            >
              Why SHUWA
            </Link>
            {MENUS.flatMap((menu) => menu.columns.flatMap((col) => col.links)).map(
              ({ title, to }) => (
                <Link
                  key={title}
                  to={to || "/for-business"}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-xl px-3 py-2.5 text-sm font-medium text-white/85 hover:bg-white/5"
                >
                  {title}
                </Link>
              )
            )}
            <Link
              to="/login?next=/business"
              onClick={() => setMobileOpen(false)}
              className="rounded-xl px-3 py-2.5 text-sm font-medium text-white/85 hover:bg-white/5"
            >
              Log in
            </Link>
            <Link
              to="/register?role=business_owner&type=business&next=/business/onboarding"
              onClick={() => setMobileOpen(false)}
              className="mt-2 rounded-full bg-primary px-4 py-2.5 text-center text-sm font-semibold text-white"
            >
              Try it for free
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
